import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PlainAction } from '@components/types';
import { isNonNullish } from '@root/helpers';
import type { QueryStatus } from '@tanstack/react-query';
import type {
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  ExpandedState,
  SortingState,
  Table,
  VisibilityState,
} from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';

import { useStableData } from '../../../../hooks';
import { TABLE_CONSTANTS } from '../../shared/constants';
import type { TableState } from '../../shared/hooks/useTableState';
import { useTableState } from '../../shared/hooks/useTableState';

import { useTableConfig } from './useTableConfig';
import { useTableStateHandlers } from './useTableStateHandlers';

export interface UseDataTableOptions<TData> {
  entityName: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  expandableData?: TData[];
  initialFilters?: ColumnFiltersState;
  initialSorting?: SortingState;
  columnVisibility?: VisibilityState;
  createEntityAction?: PlainAction;
  columnPinning?: ColumnPinningState;
  enableColumnFilters?: boolean;
  globalFilter?: string;
  pageSize?: number;
  renderCustomElements?: (
    isGroupByParent: boolean,
    setIsGroupByParent: (isGroupByParent: boolean) => void,
  ) => () => ReactNode;
  status?: QueryStatus;
  isEmpty?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
}

export interface UseDataTableReturn<TData = any> {
  table: Table<TData>;
  tableState: TableState | undefined;
  handlers: {
    setIsGroupByParent: (isGroupByParent: boolean) => void;
    handleColumnOrderChange: (activeId: string, overId: string) => void;
  };
  meta: {
    isInitialized: boolean;
    columnsCount: number;
    filteredRowsLength: number;
    totalResultsForDisplay: number;
    createEntityAction?: PlainAction;
  };
  ui: {
    expanded: ExpandedState;
    setExpanded: (expanded: ExpandedState) => void;
  };
}

/**
 * Central hook for managing TanStack Table functionality with performance optimizations.
 * Provides comprehensive table state management, event handlers, and memoized calculations.
 *
 * Key Features:
 * - Persistent state management through external store
 * - Performance-optimized with memoization and callbacks
 * - Type-safe state handlers using TanStack's Updater types
 * - Configurable pagination, sorting, filtering, and column management
 */
export function useDataTable<TData>({
  entityName,
  columns,
  createEntityAction,
  data: rawData,
  expandableData = [],
  initialFilters,
  initialSorting,
  columnVisibility = {},
  columnPinning = {},
  enableColumnFilters = true,
  globalFilter = TABLE_CONSTANTS.DEFAULT_GLOBAL_FILTER,
  pageSize = TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
}: UseDataTableOptions<TData>): UseDataTableReturn<TData> {
  // Get all column IDs from the current columns definition
  const allColumnIds = columns.map((col) => col.id).filter(isNonNullish);

  const initialState: TableState = {
    columnFilters: initialFilters ?? [],
    columnOrder: allColumnIds,
    columnPinning,
    columnVisibility: columnVisibility,
    globalFilter: globalFilter,
    isGroupByParent: TABLE_CONSTANTS.DEFAULT_IS_GROUP_BY_PARENT,
    pagination: { pageIndex: TABLE_CONSTANTS.DEFAULT_PAGINATION_INDEX, pageSize },
    sorting: initialSorting ?? [],
  };

  const {
    state: tableState,
    updateState,
    isInitialized,
  } = useTableState({
    entityName,
    initialState,
  });

  // Sync columnOrder: add missing columns to persisted by localStorage order
  useEffect(() => {
    if (!tableState?.columnOrder || !isInitialized) return;

    const persistedOrder = tableState.columnOrder;
    const missingColumns = allColumnIds.filter((id) => !persistedOrder.includes(id));

    if (missingColumns.length > 0) {
      updateState({ columnOrder: [...persistedOrder, ...missingColumns] });
    }
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const data = useStableData(rawData);
  const dataExpandable = useStableData(expandableData);

  const currentData = tableState?.isGroupByParent ? dataExpandable : data;

  const stateHandlers = useTableStateHandlers({
    tableState,
    initialState,
    updateState,
  });

  const tableConfig = useTableConfig({
    columns,
    data: currentData,
    enableColumnFilters,
    tableState,
    initialState,
    expanded,
    onExpandedChange: (updaterOrValue) => {
      const newValue = updaterOrValue instanceof Function ? updaterOrValue(expanded) : updaterOrValue;
      setExpanded(newValue);
    },
    stateHandlers,
  });

  const table = useReactTable(tableConfig);

  const setIsGroupByParent = useCallback(
    (isGroupByParent: boolean) => {
      updateState({
        isGroupByParent,
        columnVisibility: {
          ...table.getState().columnVisibility,
          toggleSubRows: isGroupByParent,
        },
      });
    },
    [table, updateState],
  );

  const handleColumnOrderChange = useCallback(
    (activeId: string, overId: string) => {
      const currentOrder = tableState?.columnOrder ?? initialState.columnOrder;
      const oldIndex = currentOrder.indexOf(activeId);
      const newIndex = currentOrder.indexOf(overId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newOrder = [...currentOrder];
        const [reorderedItem] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, reorderedItem);
        stateHandlers.onColumnOrderChange(newOrder);
      }
    },
    [tableState?.columnOrder, initialState.columnOrder, stateHandlers],
  );

  const currentColumnVisibility = table.getState().columnVisibility;
  const columnsCount =
    columns.length - Object.keys(currentColumnVisibility).filter((key) => !currentColumnVisibility[key]).length;

  const filteredRowsLength = Array.isArray(data) ? table.getFilteredRowModel().rows.length : 0;

  const totalResultsForDisplay = tableState?.isGroupByParent
    ? table.getPreExpandedRowModel().rows.reduce((prev, curr) => prev + 1 + (curr?.subRows?.length ?? 0), 0)
    : filteredRowsLength;

  return {
    table,
    tableState,
    handlers: {
      setIsGroupByParent,
      handleColumnOrderChange,
    },
    meta: {
      isInitialized,
      columnsCount,
      filteredRowsLength,
      totalResultsForDisplay,
      createEntityAction,
    },
    ui: {
      expanded,
      setExpanded,
    },
  };
}
